import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Form from '@components/forms/Form';
import {
  CareProfileGuidanceTypeEnum,
  CareProfileFragment as CareProfileType,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForGuidanceType } from '@utils/optionUtils';

type FormData = {
  guidanceType: CareProfileGuidanceTypeEnum | null;
};

type Props = {
  buttonExpand?: 'block' | 'full';
  careProfile: CareProfileType;
  onCancel?: () => void;
  onError: () => void;
  onSuccess?: (careProfile: CareProfileType | undefined) => void;
  saveKey?: string;
  skipCancel?: boolean;
};

const CareProfileGuidanceTypeForm = ({
  buttonExpand,
  careProfile,
  onCancel,
  onError,
  onSuccess,
  saveKey,
  skipCancel = false
}: Props) => {
  const initialValues: FormData = {
    guidanceType: careProfile.guidanceType ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      guidanceType: yup
        .string()
        .oneOf(Object.values(CareProfileGuidanceTypeEnum))
        .required(intl.formatMessage({ id: 'errors.careProfile.guidanceType.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...form.getValues(),
        id: careProfile?.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError,
        onSuccess: () => {
          if (response.data?.careProfileUpdate?.careProfile) {
            onSuccess?.(response.data?.careProfileUpdate.careProfile);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [careProfile?.id, form, intl, onError, onSuccess, updateCareProfile]);

  const guidanceTypeOptions = optionsForGuidanceType(intl, true);

  return (
    <Form data-test="care-profile-urgency-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <RadioButtonGroup<FormData>
        control={form.control}
        error={form.formState.errors.guidanceType}
        name="guidanceType"
        options={guidanceTypeOptions}
        required
      />
      <FormButtons
        expand={buttonExpand}
        hookForm={form}
        onCancel={onCancel}
        saveKey={saveKey}
        skipCancel={skipCancel}
      />
    </Form>
  );
};

export default CareProfileGuidanceTypeForm;
